// src/services/colorService.ts

/**
 * Service for handling theme colors and applying them to the application
 */
export interface ThemeColors {
    primary_color: string;
    secondary_color: string;
}

class ColorService {
    private colors: ThemeColors = {
        primary_color: '#119766', // Default emerald-600
        secondary_color: '#f3f4f6', // Default gray-100
    };

    private cssRoot: HTMLElement | null = null;

    constructor() {
        // Initialize CSS variables when the service is instantiated
        this.initCssVariables();
    }

    /**
     * Initialize CSS variables on the root element
     */
    private initCssVariables() {
        if (typeof window !== 'undefined') {
            this.cssRoot = document.documentElement;
            this.applyCssVariables(this.colors);
        }
    }

    /**
     * Apply CSS variables to the root element
     */
    private applyCssVariables(colors: ThemeColors) {
        if (!this.cssRoot) return;

        // Set primary color
        this.cssRoot.style.setProperty('--color-primary', colors.primary_color);
        this.cssRoot.style.setProperty('--color-primary-hover', this.adjustBrightness(colors.primary_color, -10));
        this.cssRoot.style.setProperty('--color-primary-light', this.adjustBrightness(colors.primary_color, 40));

        // Set secondary color
        this.cssRoot.style.setProperty('--color-secondary', colors.secondary_color);
        this.cssRoot.style.setProperty('--color-secondary-hover', this.adjustBrightness(colors.secondary_color, -10));
    }

    /**
     * Adjust the brightness of a color by a percentage
     * @param color Hex color
     * @param percent Percentage to adjust (-100 to 100)
     */
    private adjustBrightness(color: string, percent: number): string {
        const num = parseInt(color.replace('#', ''), 16);
        const r = (num >> 16) + percent;
        const g = ((num >> 8) & 0x00FF) + percent;
        const b = (num & 0x0000FF) + percent;

        const newR = Math.min(255, Math.max(0, r));
        const newG = Math.min(255, Math.max(0, g));
        const newB = Math.min(255, Math.max(0, b));

        return `#${(newR << 16 | newG << 8 | newB).toString(16).padStart(6, '0')}`;
    }

    /**
     * Set theme colors and apply them
     * @param colors Theme colors object
     */
    setColors(colors: ThemeColors) {
        this.colors = colors;
        this.applyCssVariables(colors);

        // Also store the colors in localStorage for persistence
        if (typeof window !== 'undefined') {
            localStorage.setItem('theme_colors', JSON.stringify(colors));
        }
    }

    /**
     * Get current theme colors
     */
    getColors(): ThemeColors {
        // Load from localStorage if available
        if (typeof window !== 'undefined') {
            const storedColors = localStorage.getItem('theme_colors');
            if (storedColors) {
                try {
                    this.colors = JSON.parse(storedColors);
                } catch (e) {
                    console.error('Failed to parse stored colors', e);
                }
            }
        }
        return this.colors;
    }
}

// Export a singleton instance
const colorService = new ColorService();
export default colorService;
